import { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../api/apiConfig';
import './Gallery.css';

const ImageCard = ({ image, onEdit, onDelete, onView }) => {
  const [imageError, setImageError] = useState(false);

  const API_BASE = getApiBaseUrl();
  const imagePath = (image.image_url || image.image_path)?.trim();
  
  let imageUrl = null;
  if (imagePath) {
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      imageUrl = imagePath;
    } else {
      const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      imageUrl = `${API_BASE}${normalizedPath}`;
    }
  }

  const handleImageError = (e) => {
    const errorDetails = {
      imageUrl,
      imagePath,
      attemptedUrl: e.target?.src,
      naturalWidth: e.target?.naturalWidth,
      naturalHeight: e.target?.naturalHeight,
      complete: e.target?.complete,
      image: image
    };
    console.error('❌ Image load error:', errorDetails);
    
    // Try to fetch the image directly to see what the actual error is
    if (imageUrl) {
      fetch(imageUrl, { method: 'HEAD', mode: 'cors' })
        .then(response => {
          console.log('✅ Image HEAD request successful:', {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries())
          });
        })
        .catch(fetchError => {
          console.error('❌ Image HEAD request failed:', fetchError);
        });
    }
    
    setImageError(true);
  };

  const handleImageLoad = (e) => {
    console.log('✅ Image loaded successfully:', {
      imageUrl,
      naturalWidth: e.target.naturalWidth,
      naturalHeight: e.target.naturalHeight,
      complete: e.target.complete
    });
    setImageError(false);
  };

  // Debug: Log image URL on mount and reset error state
  useEffect(() => {
    // Reset error state when image URL changes
    setImageError(false);
    
    if (imageUrl) {
      console.log('🖼️ Attempting to load image:', imageUrl);
      console.log('📋 Image data:', image);
      
      // Pre-validate image URL by making a HEAD request
      fetch(imageUrl, { method: 'HEAD', mode: 'cors', credentials: 'omit' })
        .then(response => {
          if (response.ok) {
            console.log('✅ Image URL is accessible (HEAD request succeeded)');
          } else {
            console.warn('⚠️ Image URL returned status:', response.status);
          }
        })
        .catch(err => {
          console.error('❌ Image URL validation failed:', err);
        });
    } else {
      console.warn('⚠️ No image URL for image:', image);
      setImageError(true);
    }
  }, [imageUrl, image]);

  return (
    <div className="image-card">
      <div className="image-card-header">
        {image.is_featured && (
          <span className="featured-badge">⭐ Featured</span>
        )}
        {image.category && (
          <span className="category-badge">{image.category}</span>
        )}
      </div>
      
      <div className="image-wrapper">
        {!imageError && imageUrl ? (
          <img
            key={imageUrl} // Force re-render when URL changes
            src={imageUrl}
            alt={image.title || image.caption || 'Gallery image'}
            onError={handleImageError}
            onLoad={handleImageLoad}
            loading="lazy"
            style={{ 
              display: 'block',
              maxWidth: '100%',
              height: 'auto'
            }}
          />
        ) : (
          <div className="image-error">
            <span>⚠️</span>
            <p>Image not found</p>
            {imageUrl && (
              <small style={{ fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
                URL: {imageUrl}
              </small>
            )}
          </div>
        )}
        
        <div className="image-overlay">
          <div className="image-actions">
            <button
              className="btn-icon btn-view"
              onClick={() => onView(image)}
              title="View image"
            >
              👁️
            </button>
            <button
              className="btn-icon btn-edit"
              onClick={() => onEdit(image)}
              title="Edit image"
            >
              ✏️
            </button>
            <button
              className="btn-icon btn-delete"
              onClick={() => onDelete(image.id)}
              title="Delete image"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>

      <div className="image-card-footer">
        <h3 className="image-title">{image.title || image.caption || 'Untitled'}</h3>
        <p className="image-date">
          {new Date(image.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default ImageCard;
